import React, { useState, useMemo, useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, Box, Typography } from '@material-ui/core';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import DashboardLayout from '../../layouts/Dashboard';
import { AtCircularProgress } from '../../components/AtCircularProgress';

import FiltersSection from './sections/FiltersSection';
import MetricsSection from './sections/MetricsSection';
import { subMonths } from 'date-fns';

import { useSentimientoFilters } from '../../hooks/useSentimientoFilters';
import {
  useSentimientoSummary,
  useSentimientoTrend,
  useSentimientoFriction,
  useSentimientoReleases,
  useSentimientoWordcloud,
} from '../../hooks/useSentimiento';

import ReleaseChart from './components/ReleaseChart';
import WordCloudChart from './components/WordCloudChart';
import TrendChart from './components/TrendChart';
import FrequentProblemsChart from './components/FrequentProblemsChart';
import ReviewsTable from './components/ReviewsTable';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: '#F3F4F6',
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
    '& .MuiCircularProgress-colorPrimary': {
      color: '#6366F1',
    },
  },
  gridRow: {
    marginBottom: theme.spacing(3),
  },
  emptyStateContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    padding: theme.spacing(6),
    textAlign: 'center',
    marginTop: theme.spacing(4),
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  emptyStateIcon: {
    fontSize: '56px',
    color: '#9CA3AF',
    marginBottom: theme.spacing(2),
  },
  emptyStateTitle: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#374151',
    marginBottom: theme.spacing(1),
  },
  emptyStateText: {
    fontSize: '14px',
    color: '#6B7280',
    maxWidth: '500px',
    margin: '0 auto',
    lineHeight: 1.6,
  },
}));

const Sentimiento = ({ t }) => {
  const classes = useStyles();

  const { data: apiResponse, isLoading: isLoadingFilters, isError } = useSentimientoFilters();
  const filters = apiResponse?.data || apiResponse;

  const [activeFilters, setActiveFilters] = useState(null);

  const defaultFilters = useMemo(() => {
    if (!filters) return null;
    const apps = filters?.apps || [];
    const endDate = new Date();
    const startDate = subMonths(endDate, 1);
    return {
      selectedApp: apps.length > 0 ? apps[0].id : '',
      selectedPlatform: '',
      selectedVersion: '',
      selectedPeriod: {
        value: "custom",
        text: "Último mes",
        period: "c",
        statePeriod: { startDate, endDate, key: "selection" }
      },
    };
  }, [filters]);

  const currentFilters = activeFilters || defaultFilters;

  const queryOpts = { enabled: true };
  const { data: summaryRes, isLoading: isLoadingSummary }     = useSentimientoSummary(currentFilters, queryOpts);
  const { data: trendRes, isLoading: isLoadingTrend }         = useSentimientoTrend(currentFilters, queryOpts);
  const { data: frictionRes, isLoading: isLoadingFriction }   = useSentimientoFriction(currentFilters, queryOpts);
  const { data: releasesRes, isLoading: isLoadingReleases }   = useSentimientoReleases(currentFilters, queryOpts);
  const { data: wordcloudRes, isLoading: isLoadingWordcloud } = useSentimientoWordcloud(currentFilters, queryOpts);

  const summaryData   = summaryRes?.data   || summaryRes;
  const trendData     = trendRes?.data     || trendRes;
  const frictionData  = frictionRes?.data  || frictionRes;
  const releasesData  = releasesRes?.data  || releasesRes;
  const wordcloudData = wordcloudRes?.data || wordcloudRes;

  const handleApplyFilters = useCallback((newFilters) => {
    setActiveFilters(newFilters);
  }, []);

  const handleClearFilters = useCallback(() => {
    setActiveFilters(null);
  }, []);


  if (isLoadingFilters) {
    return (
      <DashboardLayout title="Sentimiento">
        <Box className={classes.loadingContainer}>
          <AtCircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  if (isError || !filters) {
    return (
      <DashboardLayout title="Sentimiento">
        <Box className={classes.root}>
          <p>{t('sentiment.filters.errorLoading')}</p>
        </Box>
      </DashboardLayout>
    );
  }

  const apps = filters?.apps || [];
  if (apps.length === 0) {
    return (
      <DashboardLayout title="Sentimiento">
        <Box className={classes.root}>
          <Box className={classes.emptyStateContainer}>
            <ErrorOutlineIcon className={classes.emptyStateIcon} />
            <Typography className={classes.emptyStateTitle}>
              {t('sentiment.filters.noAppsTitle')}
            </Typography>
            <Typography className={classes.emptyStateText}>
              {t('sentiment.filters.noAppsMessage')}
            </Typography>
          </Box>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Sentimiento">
      <div className={classes.root}>

        <FiltersSection
          filters={filters}
          activeFilters={currentFilters}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
        />

        <MetricsSection summary={summaryData} isLoading={isLoadingSummary} />

        <Grid container spacing={3} className={classes.gridRow}>
            <Grid item xs={12} md={8}>
                <TrendChart data={trendData} isLoading={isLoadingTrend} />
            </Grid>
            <Grid item xs={12} md={4}>
                <FrequentProblemsChart data={frictionData} isLoading={isLoadingFriction} />
            </Grid>
        </Grid>

        <Grid container spacing={3} className={classes.gridRow}>
            <Grid item xs={12} md={6}>
                <ReleaseChart data={releasesData} isLoading={isLoadingReleases} />
            </Grid>
            <Grid item xs={12} md={6}>
                <WordCloudChart data={wordcloudData} isLoading={isLoadingWordcloud} />
            </Grid>
        </Grid>

        <Grid container spacing={3} className={classes.gridRow}>
            <Grid item xs={12}>
                <ReviewsTable filters={currentFilters} />
            </Grid>
        </Grid>
      </div>
    </DashboardLayout>
  );
};

export default withRouter(withTranslation()(Sentimiento));
